import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../../api/client";
import type { Me, TraceDocument } from "../../api/types";
import { S } from "../../styles";
import { hasRole } from "../../auth/roleHelpers";
import { POStatusBanner } from "../purchase-orders/POStatusBanner";
import { RecommendationCard } from "../recommendations/RecommendationCard";
import { SimulatePdfQuotePanel } from "../simulation/SimulatePdfQuotePanel";
import { AuditTrail } from "../audit/AuditTrail";
import { SourceDocuments } from "./SourceDocuments";
import { LoadingState, ErrorState } from "../../components/QueryStates";
import {
  TraceStatusPanel,
  traceDocsLabel,
} from "../../components/TraceStatusPanel";
import { UnitIdentity } from "../../components/OrderLine";
import { isTraceBlocking } from "../../api/types";

export function QuoteComparison({
  rfqId,
  onBack,
  me,
}: {
  rfqId: string;
  onBack: () => void;
  me: Me | null;
}) {
  const queryClient = useQueryClient();
  const canAct = hasRole(me, "buyer", "planner");
  const canViewAudit = hasRole(me, "finance", "manager");
  // Clearing a traceability block is an airworthiness decision, not a buying
  // one — the API enforces the same split and returns 403 otherwise.
  const canOverrideTrace = hasRole(me, "manager", "admin");

  // Per-quote UI state — tracked locally since mutations are shared across all rows
  const [quoteActionBusy, setQuoteActionBusy] = useState<Set<string>>(
    new Set(),
  );
  const [negotiatingQuotes, setNegotiatingQuotes] = useState<Set<string>>(
    new Set(),
  );
  const [negotiateStarted, setNegotiateStarted] = useState<Set<string>>(
    new Set(),
  );
  const [approvedPosByQuote, setApprovedPosByQuote] = useState<
    Record<
      string,
      {
        po_id: string;
        po_number: string;
        po_status: string;
        po_needs_manager_approval: boolean;
      }
    >
  >({});
  const [rejectedQuotes, setRejectedQuotes] = useState<Set<string>>(new Set());
  const [quoteActionErr, setQuoteActionErr] = useState<Record<string, string>>(
    {},
  );
  const [negotiateErr, setNegotiateErr] = useState<Record<string, string>>({});

  const {
    data: rfq,
    isLoading: rfqLoading,
    error: rfqError,
  } = useQuery({
    queryKey: ["rfq", rfqId],
    queryFn: () => api.getRFQ(rfqId),
    refetchInterval: 5_000,
  });

  const { data: quotes = [], isLoading: quotesLoading } = useQuery({
    queryKey: ["quotes", rfqId],
    queryFn: () => api.getQuotesByRFQ(rfqId),
    refetchInterval: 5_000,
  });

  const { data: rec = null } = useQuery({
    queryKey: ["recommendation", rfqId],
    queryFn: () => api.getRecommendationByRFQ(rfqId).catch(() => null),
    refetchInterval: 5_000,
  });

  // One call for every quote's source PDF, not one per card, on the same
  // interval as the quotes themselves. An individual attachment never changes
  // after it arrives — but quotes keep arriving while this screen is open, each
  // possibly carrying one, and a slower interval here meant a freshly uploaded
  // PDF quote rendered as a card with no document for up to 30 seconds.
  const { data: quoteDocs = [] } = useQuery({
    queryKey: ["quote-documents", rfqId],
    queryFn: () => api.getQuoteDocuments(rfqId),
    refetchInterval: 5_000,
  });
  const docsByQuote = new Map<string, TraceDocument[]>();
  for (const doc of quoteDocs) {
    if (!doc.quote_id) continue;
    const held = docsByQuote.get(doc.quote_id);
    if (held) held.push(doc);
    else docsByQuote.set(doc.quote_id, [doc]);
  }

  const invalidateAll = () => {
    void queryClient.invalidateQueries({ queryKey: ["quotes", rfqId] });
    void queryClient.invalidateQueries({ queryKey: ["rfq", rfqId] });
    void queryClient.invalidateQueries({ queryKey: ["recommendation", rfqId] });
  };

  // Re-running the agent pipeline. Gated on buyer/manager to match the API
  // (require_role("buyer", "manager")) — a planner can act on quotes but would
  // get a 403 here, so the button is not offered to them.
  const canReanalyze = hasRole(me, "buyer", "manager");
  const [reanalyzeMsg, setReanalyzeMsg] = useState<string | null>(null);
  const [reanalyzeErr, setReanalyzeErr] = useState<string | null>(null);
  const reanalyzeMutation = useMutation({
    mutationFn: () => api.reanalyzeRFQ(rfqId),
    onMutate: () => {
      setReanalyzeErr(null);
      setReanalyzeMsg(null);
    },
    onSuccess: (res) => {
      setReanalyzeMsg(res.message);
      invalidateAll();
    },
    onError: (e: unknown) =>
      setReanalyzeErr(
        e instanceof ApiError ? e.detail : "Could not start the analysis.",
      ),
  });

  // Per-quote, because several quotes on one RFQ can be flagged and the buyer
  // judges each on its own merits.
  const [ackByQuote, setAckByQuote] = useState<Record<string, boolean>>({});
  // Separate from ackByQuote on purpose: a buyer acknowledging a wrong part
  // number has not thereby accepted missing paperwork, and only a manager can
  // set this one.
  const [traceAckByQuote, setTraceAckByQuote] = useState<
    Record<string, boolean>
  >({});

  const approveMutation = useMutation({
    mutationFn: (quoteId: string) =>
      api.approveQuote(
        quoteId,
        !!ackByQuote[quoteId],
        !!traceAckByQuote[quoteId],
      ),
  });

  const rejectMutation = useMutation({
    mutationFn: (quoteId: string) => api.rejectQuote(quoteId),
  });

  const negotiateMutation = useMutation({
    mutationFn: (quoteId: string) => api.startNegotiation(rfqId, quoteId),
  });

  const handleApproveQuote = (quoteId: string) => {
    setQuoteActionBusy((prev) => new Set([...prev, quoteId]));
    setQuoteActionErr((prev) => {
      const n = { ...prev };
      delete n[quoteId];
      return n;
    });
    approveMutation.mutate(quoteId, {
      onSuccess: (result) => {
        setApprovedPosByQuote((prev) => ({ ...prev, [quoteId]: result }));
        invalidateAll();
      },
      onError: (e) => {
        setQuoteActionErr((prev) => ({ ...prev, [quoteId]: e.message }));
      },
      onSettled: () => {
        setQuoteActionBusy((prev) => {
          const s = new Set(prev);
          s.delete(quoteId);
          return s;
        });
      },
    });
  };

  const handleRejectQuote = (quoteId: string) => {
    setQuoteActionBusy((prev) => new Set([...prev, quoteId]));
    setQuoteActionErr((prev) => {
      const n = { ...prev };
      delete n[quoteId];
      return n;
    });
    rejectMutation.mutate(quoteId, {
      onSuccess: () => {
        setRejectedQuotes((prev) => new Set([...prev, quoteId]));
        invalidateAll();
      },
      onError: (e) => {
        setQuoteActionErr((prev) => ({ ...prev, [quoteId]: e.message }));
      },
      onSettled: () => {
        setQuoteActionBusy((prev) => {
          const s = new Set(prev);
          s.delete(quoteId);
          return s;
        });
      },
    });
  };

  const handleNegotiateQuote = (quoteId: string) => {
    setNegotiatingQuotes((prev) => new Set([...prev, quoteId]));
    setNegotiateErr((prev) => {
      const n = { ...prev };
      delete n[quoteId];
      return n;
    });
    negotiateMutation.mutate(quoteId, {
      onSuccess: () => {
        setNegotiateStarted((prev) => new Set([...prev, quoteId]));
      },
      onError: (e) => {
        setNegotiateErr((prev) => ({ ...prev, [quoteId]: e.message }));
      },
      onSettled: () => {
        setNegotiatingQuotes((prev) => {
          const s = new Set(prev);
          s.delete(quoteId);
          return s;
        });
      },
    });
  };

  if (rfqLoading || quotesLoading)
    return <LoadingState label="Loading quotes…" />;
  if (rfqError)
    return (
      <div style={S.card}>
        <ErrorState error={rfqError} />
        <button onClick={onBack} style={{ marginTop: 8 }}>
          ← Back
        </button>
      </div>
    );

  const sorted = [...quotes].sort(
    (a, b) => parseFloat(a.unit_price) - parseFloat(b.unit_price),
  );

  // Once an RFQ is awarded (a PO has been created for one of its quotes) the
  // whole quote set is frozen: no other quote can be approved, negotiated, or
  // rejected. Mirrors the backend award-lock (RFQ status Awarding/Awarded).
  const rfqAwarded = rfq?.status === "Awarding" || rfq?.status === "Awarded";
  const awardLockTitle =
    "This RFQ has already been awarded — quotes are locked";

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 20,
        }}
      >
        <button onClick={onBack} style={S.btnSm}>
          ← Back
        </button>
        <div>
          <h2 style={{ ...S.h2, margin: 0 }}>{rfq?.rfq_number ?? rfqId}</h2>
          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              marginTop: 2,
            }}
          >
            {rfq && <span style={S.statusBadge(rfq.status)}>{rfq.status}</span>}
            {rfq?.part_number && (
              <span style={{ ...S.muted, fontSize: 12 }}>
                PN:{" "}
                <span style={{ fontWeight: 600, color: "#333" }}>
                  {rfq.part_number}
                </span>
              </span>
            )}
          </div>
        </div>
        <button
          onClick={invalidateAll}
          style={{ ...S.btnSm, marginLeft: "auto" }}
        >
          ↺ Refresh
        </button>
      </div>

      {quotes.length === 0 && (
        <div style={S.card}>
          <p style={S.muted}>
            {rfq?.status === "Awaiting" || rfq?.status === "Sent"
              ? "Waiting for supplier quotes… (polling every 5s)"
              : "No quotes received for this RFQ."}
          </p>
        </div>
      )}

      {sorted.length > 0 && (
        <div>
          <p style={{ ...S.muted, marginBottom: 12 }}>
            {sorted.length} quote{sorted.length !== 1 ? "s" : ""} received. Best
            price first.
          </p>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))",
              gap: 14,
            }}
          >
            {sorted.map((q, i) => (
              <div
                key={q.id}
                style={{
                  ...S.card,
                  marginBottom: 0,
                  border: rejectedQuotes.has(q.id)
                    ? "1px solid #ffc9c9"
                    : approvedPosByQuote[q.id]
                      ? "2px solid #2f9e44"
                      : i === 0
                        ? "2px solid #4f46e5"
                        : "1px solid #e8eaf0",
                  opacity: rejectedQuotes.has(q.id) ? 0.55 : 1,
                  position: "relative",
                }}
              >
                {i === 0 && (
                  <div
                    style={{
                      position: "absolute",
                      top: -10,
                      right: 12,
                      background: "#4f46e5",
                      color: "#fff",
                      fontSize: 11,
                      fontWeight: 700,
                      padding: "2px 8px",
                      borderRadius: 4,
                    }}
                  >
                    BEST PRICE
                  </div>
                )}
                <h3 style={S.h3} title={q.supplier_id}>
                  {q.supplier_name ?? `Supplier ${q.supplier_id.slice(0, 8)}…`}
                </h3>
                {/*
                  Directly under the supplier, above the figures: when a quote
                  was read off a PDF, the figures on this card are an extraction
                  and the document is what they were extracted from. A buyer
                  awarding on price should be able to reach the paper, for the
                  same reason receiving shows the certificate and not only the
                  verdict.
                */}
                <SourceDocuments
                  documents={docsByQuote.get(q.id) ?? []}
                  sourceChannel={q.trace_claim?.source_channel}
                />
                {q.needs_review && (
                  // Deliberately above the figures: this quote can still be the
                  // cheapest and carry the BEST PRICE badge, so the caveat has
                  // to be read before the price, not after it.
                  <div
                    role="alert"
                    style={{
                      background: "#fff4e6",
                      border: "1px solid #ffd8a8",
                      borderRadius: 4,
                      padding: "6px 8px",
                      margin: "0 0 8px",
                      fontSize: 12,
                      color: "#8a5a00",
                    }}
                  >
                    <strong>⚠ Needs review</strong>
                    {q.review_reason ? <> — {q.review_reason}</> : null}
                    {canAct && !rfqAwarded && (
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          marginTop: 6,
                          fontWeight: 600,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={!!ackByQuote[q.id]}
                          onChange={(e) =>
                            setAckByQuote((prev) => ({
                              ...prev,
                              [q.id]: e.target.checked,
                            }))
                          }
                        />
                        I have checked this and want to order it anyway
                      </label>
                    )}
                  </div>
                )}
                <TraceStatusPanel
                  status={q.trace_status}
                  findings={q.trace_findings}
                  claim={q.trace_claim}
                  canOverride={canOverrideTrace}
                  canAct={canAct && !rfqAwarded}
                  acknowledged={!!traceAckByQuote[q.id]}
                  onAcknowledge={(v) =>
                    setTraceAckByQuote((prev) => ({ ...prev, [q.id]: v }))
                  }
                />
                <dl
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "4px 10px",
                    margin: 0,
                    fontSize: 13,
                  }}
                >
                  <dt style={{ color: "#666" }}>Part No.</dt>
                  <dd style={{ margin: 0, fontWeight: 600 }}>
                    {q.part_number}
                  </dd>
                  <dt style={{ color: "#666" }}>Unit Price</dt>
                  <dd
                    style={{
                      margin: 0,
                      fontWeight: 700,
                      color: i === 0 ? "#4f46e5" : undefined,
                    }}
                  >
                    {q.currency} {parseFloat(q.unit_price).toFixed(2)}
                  </dd>
                  <dt style={{ color: "#666" }}>Qty</dt>
                  <dd style={{ margin: 0 }}>{q.quantity}</dd>
                  <dt style={{ color: "#666" }}>Condition</dt>
                  <dd style={{ margin: 0 }}>{q.condition_code}</dd>
                  <dt style={{ color: "#666" }}>Lead Time</dt>
                  <dd style={{ margin: 0 }}>
                    {q.lead_time_days !== null
                      ? `${q.lead_time_days} days`
                      : "—"}
                  </dd>
                  <dt style={{ color: "#666" }}>Trace Docs</dt>
                  <dd style={{ margin: 0 }}>
                    {traceDocsLabel(q.trace_claim, q.has_trace)}
                  </dd>
                  {/*
                    Only rendered when the supplier named one. Most quotes have
                    no serial — new stock is picked after the order — and a row
                    reading "not yet identified" on every quote on the page
                    would be noise on the screen where quotes are compared.
                    On the resulting order it is shown either way, because
                    there it is a thing receiving has to resolve.
                  */}
                  {((q.serial_numbers?.length ?? 0) > 0 || q.batch_number) && (
                    <>
                      <dt style={{ color: "#666" }}>
                        {q.batch_number ? "Lot" : "Serial"}
                      </dt>
                      <dd style={{ margin: 0 }}>
                        <UnitIdentity
                          serialNumbers={q.serial_numbers}
                          batchNumber={q.batch_number}
                          quantity={q.quantity}
                        />
                      </dd>
                    </>
                  )}
                  <dt style={{ color: "#666" }}>Validity</dt>
                  <dd style={{ margin: 0 }}>{q.validity_date ?? "—"}</dd>
                </dl>
                {q.notes && (
                  <p
                    style={{
                      ...S.muted,
                      marginTop: 8,
                      fontSize: 12,
                      borderTop: "1px solid #f0f0f0",
                      paddingTop: 8,
                    }}
                  >
                    {q.notes}
                  </p>
                )}
                {canAct &&
                  !rejectedQuotes.has(q.id) &&
                  !approvedPosByQuote[q.id] && (
                    <div
                      style={{
                        marginTop: 12,
                        display: "flex",
                        gap: 6,
                        flexWrap: "wrap" as const,
                      }}
                    >
                      <button
                        title={rfqAwarded ? awardLockTitle : undefined}
                        style={{
                          ...S.btnSm,
                          flex: 1,
                          background: "#2f9e44",
                          cursor: rfqAwarded ? "not-allowed" : undefined,
                          opacity:
                            quoteActionBusy.has(q.id) || rfqAwarded ? 0.5 : 1,
                        }}
                        disabled={
                          quoteActionBusy.has(q.id) ||
                          rfqAwarded ||
                          (!!q.needs_review && !ackByQuote[q.id]) ||
                          (isTraceBlocking(q.trace_status) &&
                            !traceAckByQuote[q.id])
                        }
                        onClick={() => handleApproveQuote(q.id)}
                      >
                        ✓ Approve
                      </button>
                      <button
                        title={rfqAwarded ? awardLockTitle : undefined}
                        style={{
                          ...S.btnSm,
                          flex: 1,
                          background: "#7c3aed",
                          cursor: rfqAwarded ? "not-allowed" : undefined,
                          opacity:
                            negotiatingQuotes.has(q.id) ||
                            negotiateStarted.has(q.id) ||
                            rfqAwarded
                              ? 0.5
                              : 1,
                        }}
                        disabled={
                          negotiatingQuotes.has(q.id) ||
                          negotiateStarted.has(q.id) ||
                          rfqAwarded
                        }
                        onClick={() => handleNegotiateQuote(q.id)}
                      >
                        {negotiatingQuotes.has(q.id)
                          ? "…"
                          : negotiateStarted.has(q.id)
                            ? "🤝 Started"
                            : "🤝 Negotiate"}
                      </button>
                      <button
                        title={rfqAwarded ? awardLockTitle : undefined}
                        style={{
                          ...S.btnSm,
                          flex: 1,
                          background: "#c92a2a",
                          cursor: rfqAwarded ? "not-allowed" : undefined,
                          opacity:
                            quoteActionBusy.has(q.id) || rfqAwarded ? 0.5 : 1,
                        }}
                        disabled={quoteActionBusy.has(q.id) || rfqAwarded}
                        onClick={() => handleRejectQuote(q.id)}
                      >
                        ✗ Reject
                      </button>
                    </div>
                  )}
                {canAct &&
                  rfqAwarded &&
                  !approvedPosByQuote[q.id] &&
                  !rejectedQuotes.has(q.id) && (
                    <p
                      style={{
                        ...S.muted,
                        marginTop: 6,
                        marginBottom: 0,
                        fontSize: 11,
                      }}
                    >
                      {q.status === "selected"
                        ? "🏆 This quote was awarded."
                        : "🔒 RFQ awarded — this quote is locked."}
                    </p>
                  )}
                {approvedPosByQuote[q.id] && (
                  <div style={{ marginTop: 8 }}>
                    <POStatusBanner
                      poNumber={approvedPosByQuote[q.id].po_number}
                      poStatus={approvedPosByQuote[q.id].po_status}
                      needsManager={
                        approvedPosByQuote[q.id].po_needs_manager_approval
                      }
                    />
                  </div>
                )}
                {rejectedQuotes.has(q.id) && (
                  <div
                    style={{
                      marginTop: 8,
                      padding: "4px 8px",
                      background: "#fff0f0",
                      borderRadius: 4,
                      color: "#c92a2a",
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    ✗ Rejected
                  </div>
                )}
                {(negotiateErr[q.id] || quoteActionErr[q.id]) && (
                  <p style={{ ...S.err, marginTop: 4, fontSize: 12 }}>
                    ⚠ {negotiateErr[q.id] || quoteActionErr[q.id]}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={S.card}>
        <h3 style={{ ...S.h3, marginBottom: 4 }}>
          📄 Simulate Supplier PDF Quote
        </h3>
        <p style={{ ...S.muted, marginBottom: 0 }}>
          Upload a PDF or image quote to exercise the Tika + VLM extraction
          pipeline. The extracted quote will appear in the grid above.
        </p>
        <SimulatePdfQuotePanel rfqId={rfqId} onUploaded={invalidateAll} />
      </div>

      <div style={{ marginTop: 28 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            marginBottom: 12,
          }}
        >
          <h3 style={{ ...S.h3, marginBottom: 0 }}>🤖 AI Recommendation</h3>
          {/* Offered whenever there are quotes to analyse and the RFQ is not
              already awarded — the same two conditions the API enforces. The
              recovery case is an RFQ with quotes but no recommendation, but
              re-running over a stale one is legitimate too, so the button is
              not hidden once a recommendation exists. */}
          {canReanalyze && quotes.length > 0 && !rfqAwarded && (
            <button
              style={{
                ...S.btnSm,
                opacity: reanalyzeMutation.isPending ? 0.5 : 1,
                cursor: reanalyzeMutation.isPending ? "wait" : "pointer",
              }}
              disabled={reanalyzeMutation.isPending}
              onClick={() => reanalyzeMutation.mutate()}
              title={
                rec
                  ? "Score these quotes again and produce a fresh recommendation"
                  : "Run the scoring and recommendation agents over the quotes received"
              }
            >
              {reanalyzeMutation.isPending
                ? "Starting…"
                : rec
                  ? "↻ Re-run analysis"
                  : "▶ Run analysis"}
            </button>
          )}
        </div>
        {reanalyzeMsg && <p style={S.ok}>✓ {reanalyzeMsg}</p>}
        {reanalyzeErr && <p style={S.err}>⚠ {reanalyzeErr}</p>}
        {rec ? (
          <RecommendationCard
            rec={rec}
            partNumber={rfq?.part_number ?? quotes[0]?.part_number ?? null}
            canAction={hasRole(me, "buyer", "planner")}
            onAction={invalidateAll}
            awardLocked={rfqAwarded}
            canOverrideTrace={canOverrideTrace}
          />
        ) : (
          <div style={S.card}>
            <p style={S.muted}>
              {rfq?.status === "Quoted"
                ? "Agent pipeline is running… check back in a moment."
                : rfq?.status === "Sent" || rfq?.status === "Awaiting"
                  ? "Waiting for supplier quotes before AI analysis can run."
                  : quotes.length > 0
                    ? "No AI recommendation for this RFQ — the agents never ran over these quotes."
                    : "No AI recommendation available for this RFQ."}
            </p>
          </div>
        )}
      </div>

      {canViewAudit && (
        <div style={{ marginTop: 28 }}>
          <h3 style={{ ...S.h3, marginBottom: 12 }}>🕓 Activity History</h3>
          <AuditTrail resourceType="rfq" resourceId={rfqId} />
        </div>
      )}
    </div>
  );
}
